import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MapPin, Mail, SendHorizonal, LoaderCircle } from "lucide-react";
import { Reveal, Overline } from "@/components/Shared";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICE_OPTIONS = [
  "Incubation Support",
  "Corporate Innovation Programs",
  "Investor Network",
  "Patent & IP Advisory",
  "B2G Strategy",
  "AI & DPDP Skilling",
  "Global Market Entry",
  "General Enquiry",
];

const initialForm = { name: "", email: "", company: "", service: SERVICE_OPTIONS[0], message: "" };

const inputCls =
  "w-full border border-[rgba(212,175,55,0.2)] bg-[#070E1B]/70 px-4 py-3.5 text-sm text-[#F7F5F0] placeholder:text-[#64748B] outline-none transition-colors duration-300 focus:border-[#D4AF37]";

export default function Contact() {
  const [form, setForm] = useState(initialForm);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    const onPreselect = (e) => {
      if (SERVICE_OPTIONS.includes(e.detail)) {
        setForm((f) => ({ ...f, service: e.detail }));
      }
    };
    window.addEventListener("byot:preselect-service", onPreselect);
    return () => window.removeEventListener("byot:preselect-service", onPreselect);
  }, []);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Enquiry received. Our strategy desk will respond within one business day.");
      setForm(initialForm);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not send your enquiry. Please email us directly.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="mx-auto max-w-7xl px-5 py-24 sm:px-8 md:py-32">
      <div className="grid gap-14 lg:grid-cols-[1fr_1.15fr] lg:gap-20">
        <div>
          <Reveal>
            <Overline>Strategic Enquiry</Overline>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 data-testid="contact-heading" className="mt-4 font-serif text-3xl tracking-tight text-[#F7F5F0] sm:text-4xl lg:text-5xl">
              Begin the <em className="gold-text not-italic">conversation.</em>
            </h2>
          </Reveal>
          <Reveal delay={0.18}>
            <p className="mt-6 max-w-md text-base leading-relaxed text-[#94A3B8]">
              Every engagement starts with a confidential consultation. Share your mandate , 
              the CEO's desk reviews each enquiry personally.
            </p>
          </Reveal>

          <div className="mt-10 flex flex-col gap-6">
            <Reveal delay={0.24}>
              <div data-testid="contact-office" className="flex items-start gap-4">
                <MapPin size={20} className="mt-1 shrink-0 text-[#D4AF37]" />
                <div>
                  <p className="font-mono-alt text-[10px] uppercase tracking-[0.28em] text-[#D4AF37]">Registered Office</p>
                  <p className="mt-2 text-sm leading-relaxed text-[#CBD5E1]">
                    G-1321, Third Floor, Chittaranjan Park,<br />New Delhi, 110019, India
                  </p>
                </div>
              </div>
            </Reveal>
            <Reveal delay={0.3}>
              <div data-testid="contact-email-block" className="flex items-start gap-4">
                <Mail size={20} className="mt-1 shrink-0 text-[#D4AF37]" />
                <div>
                  <p className="font-mono-alt text-[10px] uppercase tracking-[0.28em] text-[#D4AF37]">Direct Line</p>
                  <a
                    href="mailto:bhumikavyas@byotinnovations.com"
                    data-testid="contact-email-link"
                    className="mt-2 block text-sm text-[#CBD5E1] transition-colors hover:text-[#D4AF37]"
                  >
                    bhumikavyas@byotinnovations.com
                  </a>
                </div>
              </div>
            </Reveal>
          </div>
        </div>

        <Reveal delay={0.15}>
          <form
            data-testid="contact-form"
            onSubmit={submit}
            className="grain relative border border-[rgba(212,175,55,0.25)] bg-[#0F1A2E]/85 p-7 backdrop-blur-md sm:p-10"
          >
            <div className="grid gap-5 sm:grid-cols-2">
              <div>
                <label htmlFor="contact-name" className="mb-2 block font-mono-alt text-[10px] uppercase tracking-[0.25em] text-[#94A3B8]">Full Name *</label>
                <input id="contact-name" data-testid="contact-name-input" required minLength={2} value={form.name} onChange={set("name")} placeholder="Your name" className={inputCls} />
              </div>
              <div>
                <label htmlFor="contact-email" className="mb-2 block font-mono-alt text-[10px] uppercase tracking-[0.25em] text-[#94A3B8]">Email *</label>
                <input id="contact-email" data-testid="contact-email-input" type="email" required value={form.email} onChange={set("email")} placeholder="you@company.com" className={inputCls} />
              </div>
              <div>
                <label htmlFor="contact-company" className="mb-2 block font-mono-alt text-[10px] uppercase tracking-[0.25em] text-[#94A3B8]">Organisation</label>
                <input id="contact-company" data-testid="contact-company-input" value={form.company} onChange={set("company")} placeholder="Company / Institution" className={inputCls} />
              </div>
              <div>
                <label htmlFor="contact-service" className="mb-2 block font-mono-alt text-[10px] uppercase tracking-[0.25em] text-[#94A3B8]">Area of Interest</label>
                <select id="contact-service" data-testid="contact-service-select" value={form.service} onChange={set("service")} className={inputCls}>
                  {SERVICE_OPTIONS.map((o) => (
                    <option key={o} value={o} className="bg-[#0F1A2E]">{o}</option>
                  ))}
                </select>
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="contact-message" className="mb-2 block font-mono-alt text-[10px] uppercase tracking-[0.25em] text-[#94A3B8]">Your Mandate *</label>
                <textarea id="contact-message" data-testid="contact-message-input" required minLength={10} rows={5} value={form.message} onChange={set("message")} placeholder="Tell us what you are building, entering or protecting…" className={`${inputCls} resize-none`} />
              </div>
            </div>
            <button
              type="submit"
              data-testid="contact-submit-btn"
              disabled={sending}
              className="group mt-7 flex w-full items-center justify-center gap-3 bg-[#D4AF37] px-7 py-4 font-mono-alt text-[11px] uppercase tracking-[0.25em] text-[#070E1B] transition-colors duration-300 hover:bg-[#F3E5AB] disabled:cursor-not-allowed disabled:opacity-60"
            >
              {sending ? (
                <>
                  <LoaderCircle size={16} className="animate-spin" /> Dispatching…
                </>
              ) : (
                <>
                  Submit Enquiry
                  <SendHorizonal size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
                </>
              )}
            </button>
            <p className="mt-4 text-center text-[11px] leading-relaxed text-[#64748B]">
              Confidential. No obligation. Response within one business day.
            </p>
          </form>
        </Reveal>
      </div>
    </section>
  );
}
